import {useEffect, useState} from 'react';
import ThemeProvider from './theme/ThemeProvider';
import {observer} from 'mobx-react-lite';
import {toastStore} from './stores/toastStore';

// import components
import {SdbDefaultTemplate} from '@sdb/ui/components/templates/SdbDefault/index';
import Snackbar from '@sdb/ui/components/atoms/Snackbar/index';
import Alert from '@sdb/ui/components/atoms/Alert/index';
import AnnounceKit from '@sdb/ui/components/atoms/AnnounceKit/index';
import {I18nTransAtomComponent} from '@sdb/ui/translate/atoms/Trans/Trans.atom';
import useEnvironmentContext from '@sdb/ui/components/providers/Environment/useEnvironmentContext';

import '@sdb/ui/fonts';

import {store} from './store';
import SHA1 from 'sha1-es/src/sha1.js';
import {initI18n} from './localize/initi18n';
import {updateUserDetails, updateUserDetailsCentral} from './updateUserDetails/updateUserDetails';

// import stores
import featureDiscoveryStore from '@sdb/ui/components/templates/FeatureDiscovery/FeatureDiscovery.store';
import announcekitStore from '@sdb/ui/client-store/announcekit.store';
import {messageSummaryStore} from './stores/messageSummaryStore';

// import hooks
import {useTranslation} from '@sdb/ui/translate/hooks/index';
import {useUserSettings} from './hooks/useUserSettings.hook';
import {useNavigation} from './hooks/useNavigation.hook';
import {useAcaAppLitElement} from './hooks/useAcaAppLitElement.hook';
import {useBreadcrumbs} from './hooks/useBreadcrumbs.hook';
import useInitializeTheme from './hooks/useInitializeTheme.hook';

// import types
import type {AppSwitcherMenuItem} from '@sdb/ui/types/components/molecules/AppSwitcherMenu.types';
import type {AcaAppElement} from './types/AcaAppElement';
import type {NavigationRailMenuData} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';
import type {PropsWithChildren, RefObject} from 'react';

initI18n();

interface TestMenuProps {
    litRef: RefObject<AcaAppElement | null>;
}

const LearningWrapper = observer(({children, litRef}: PropsWithChildren<TestMenuProps>) => {
    const {t} = useTranslation();
    const [showAnnounceKit, setShowAnnounceKit] = useState(false);

    useInitializeTheme();

    const {isVisible} = useAcaAppLitElement(litRef);
    const {isLoading: isUserSettingsLoading, data: userSettingsData} = useUserSettings();

    const user = userSettingsData?.user;
    const settingsTenantId = userSettingsData?.tenantId;

    const {navData, navigate, selectedId} = useNavigation(userSettingsData?.menu);
    const breadcrumbs = useBreadcrumbs(navData, selectedId ?? undefined);

    const {userId, tenantId, setUserId, setTenantId} = useEnvironmentContext();

    useEffect(() => {
        if (user?.idString && userId !== user?.idString) {
            setUserId(user.idString);
        }
        if (tenantId !== settingsTenantId) {
            setTenantId(settingsTenantId);
        }
    }, [userId, tenantId, user, settingsTenantId]);

    useEffect(() => {
        if (user) {
            messageSummaryStore.startPolling();
        }
        return () => messageSummaryStore.stopPolling();
    }, [user]);

    // Delay AnnounceKit rendering
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (user) {
            timer = setTimeout(() => {
                setShowAnnounceKit(!!user);
                updateUserDetails();
                updateUserDetailsCentral();
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [user?.idString]);

    return (
        <>
            <ThemeProvider>
                <SdbDefaultTemplate
                    hideTemplate={!isVisible}
                    appSwitcher={{data: userSettingsData?.applications as AppSwitcherMenuItem[]}}
                    logo={{src: userSettingsData?.tenantLogoUrl ?? undefined}}
                    search={{
                        overwriteClick: () => {
                            navigate('/search');
                        },
                    }}
                    featureDiscovery={
                        isUserSettingsLoading || !isVisible
                            ? undefined
                            : {
                                  featureDiscoveryName: 'sdb-ui',
                                  skipIds:
                                      (userSettingsData?.applications?.length ?? 0) > 1
                                          ? []
                                          : ['appSwitcher'],
                                  overrides: [
                                      {
                                          id: 'start',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.start.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.start.description" />
                                          ),
                                      },
                                      {
                                          id: 'navigationRail',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.navigationRail.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.navigationRail.description" />
                                          ),
                                      },
                                      {
                                          id: 'appSwitcher',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.appSwitcher.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.appSwitcher.description" />
                                          ),
                                      },
                                      {
                                          id: 'breadcrumbs',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.breadcrumbs.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.breadcrumbs.description" />
                                          ),
                                      },
                                      {
                                          id: 'notifications',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.notifications.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.notifications.description" />
                                          ),
                                      },
                                      {
                                          id: 'profileMenuButton',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.profileMenuButton.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.profileMenuButton.description" />
                                          ),
                                      },
                                      {
                                          id: 'restartButton',
                                          title: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.restartButton.title" />
                                          ),
                                          description: (
                                              <I18nTransAtomComponent i18nKey="featureDiscovery.steps.restartButton.description" />
                                          ),
                                      },
                                  ],
                                  text: {
                                      previous: t('featureDiscovery.previous'),
                                      next: t('featureDiscovery.next'),
                                      done: t('featureDiscovery.done'),
                                      pagination: ({currentStep, totalSteps}) =>
                                          t('featureDiscovery.pagination', {
                                              currentStep: currentStep + 1,
                                              totalSteps,
                                          }),
                                  },
                              }
                    }
                    breadcrumbs={{data: breadcrumbs}}
                    profile={{
                        header: {
                            avatarProps: {
                                src: user?.avatarUrl,
                            },
                            title: t('profile'),
                            email: user?.email,
                            emailProps: {
                                linkProps: {
                                    href: 'mailto:' + user?.email,
                                },
                            },
                            name: user?.displayName,
                        },
                        data: [
                            {
                                items: [
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('myInformation'),
                                        icon: 'address-card',
                                        payload: () => {
                                            navigate('/user/my/profile');
                                        },
                                    },
                                ],
                            },
                            {
                                title: 'SDB Leerplatform',
                                items: [
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('help'),
                                        icon: 'question',
                                        payload: () => {
                                            window.open(
                                                'https://support.sdbgroep.nl/portal/nl/kb/sdb-leerplatform',
                                                '_blank',
                                            );
                                        },
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('releaseNotes'),
                                        icon: 'gift',
                                        badge:
                                            announcekitStore.unreads > 0
                                                ? {
                                                      color: 'error',
                                                      badgeContent: announcekitStore.unreads,
                                                  }
                                                : undefined,
                                        payload: () => {
                                            announcekitStore.open();
                                        },
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('uservoiceIcon'),
                                        icon: 'wand-magic-sparkles',
                                        payload: () => {
                                            window.open('/go-to-uservoice', '_blank');
                                        },
                                    },
                                    {
                                        selected: false,
                                        type: 'card',
                                        label: t('statusPage'),
                                        icon: 'gauge-high',
                                        payload: () => {
                                            window.open('https://status.sdbgroep.nl', '_blank');
                                        },
                                    },
                                    {
                                        type: 'card',
                                        label: t('restartFeatureDiscovery'),
                                        icon: 'graduation-cap',
                                        dataTourId: 'FeatureDiscoverRestartButton',
                                        selected: false,
                                        payload: () => {
                                            featureDiscoveryStore.restart();
                                        },
                                    },
                                ],
                            },
                        ],
                        footer: {
                            withLove: true,
                            logoutButtonProps: {
                                children: t('signOut'),
                                onClick: () => {
                                    store.dispatch({type: 'SIGNOUT'});
                                },
                            },
                        },
                    }}
                    notifications={{
                        badge: {
                            badgeContent: messageSummaryStore.unreadCount,
                            color: 'warning',
                        },
                        header: {title: t('notifications')},
                        isLoading: messageSummaryStore.isLoading,
                        data: [
                            {
                                title: t('messages'),
                                noItemsLabel: t('noMessages'),

                                payload: () => {
                                    navigate('/message/inbox/received');
                                },
                                badge: {
                                    badgeContent: messageSummaryStore.unreadCount,
                                    color: 'warning',
                                },
                                items: messageSummaryStore.data?.firstUnreadMessages?.map((x) => ({
                                    label: x.subject || '', // Already translated by the store
                                    payload: () => {
                                        navigate(
                                            `/message/inbox/detail/${encodeURIComponent(encodeURIComponent(x.id))}/message`,
                                        );
                                    },
                                    badge: {color: 'warning'},
                                })),
                            },
                        ],
                    }}
                    navigationRail={{
                        withPinSwitch: false,
                        data: navData as NavigationRailMenuData,
                        selectedId: selectedId ?? undefined,
                        isLoading: isUserSettingsLoading,
                    }}
                >
                    {children}
                </SdbDefaultTemplate>
            </ThemeProvider>
            {showAnnounceKit && (
                <AnnounceKit
                    user={{id: `lp_${SHA1.hash(user?.idString)}`}}
                    data={{application: 'SDB Leerplatform'}}
                    widget="https://announcekit.co/widgets/v2/2oPj8s"
                />
            )}
            <Snackbar
                key={new Date().toISOString()}
                autoHideDuration={toastStore.currentToast?.duration}
                open={!!toastStore.currentToast}
                onClose={() => toastStore.clearToast()}
                anchorOrigin={{
                    horizontal: 'left',
                    vertical: 'bottom',
                }}
            >
                <Alert severity={toastStore.currentToast?.severity}>
                    {toastStore.currentToast?.message}
                </Alert>
            </Snackbar>
        </>
    );
});

export default LearningWrapper;
